/**
 * The usage beacon — quiet, lossy, and cheap by design.
 *
 * Accumulates app opens and per-screen seconds locally and posts the deltas
 * about once a minute, plus a keepalive send when the app goes to the
 * background (which on a phone is the moment that matters — nobody closes
 * apps, they leave them). A failed post is simply dropped back into the
 * accumulator: analytics must never queue behind a score, retry into a dead
 * zone, or cost a battery anything measurable.
 *
 * This exists so ONE screen can answer "is anybody actually using this, and
 * who has never opened it" — see the Phones section on the Setup tab.
 */

import { getState } from './store'

const TICK_S = 5
const FLUSH_MS = 60_000

let screen = 'score'
let opens = 0
let seconds: Record<string, number> = {}
let started = false

export function setScreen(name: string): void {
  screen = name
}

async function flush(keepalive = false): Promise<void> {
  const token = getState().adminToken ?? getState().token
  if (!token) return
  if (opens === 0 && Object.keys(seconds).length === 0) return
  const payload = {
    day: new Date().toLocaleDateString('en-CA'),
    opens,
    screens: seconds,
  }
  // Taken off the ledger BEFORE the send: a slow request must not double-count
  // the ticks that land while it is in flight.
  opens = 0
  seconds = {}
  try {
    const res = await fetch('/api/usage', {
      method: 'POST',
      keepalive,
      headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
      body: JSON.stringify(payload),
    })
    if (!res.ok) throw new Error(String(res.status))
  } catch {
    // Put the deltas back; they ride the next beacon. Losing a few seconds
    // to a race here is fine — this is a usage sketch, not a ledger.
    opens += payload.opens
    for (const [k, v] of Object.entries(payload.screens))
      seconds[k] = (seconds[k] ?? 0) + v
  }
}

export function startUsage(): void {
  if (started || typeof document === 'undefined') return
  started = true

  if (document.visibilityState === 'visible') opens += 1

  window.setInterval(() => {
    if (document.visibilityState === 'visible') {
      seconds[screen] = (seconds[screen] ?? 0) + TICK_S
    }
  }, TICK_S * 1000)

  window.setInterval(() => void flush(), FLUSH_MS)

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') opens += 1
    // Going dark is the one reliable moment to get the tail out the door.
    else void flush(true)
  })
}
